import { useState } from 'react';
import type { CSSProperties } from 'react';
import GlassCard from '../components/GlassCard';

export type ClaimsDisplayProps = {
  /** Decoded claims object */
  claims: Record<string, unknown>;
  /** Optional pre-extracted fields for nicer display */
  fields?: {
    email?: string;
    name?: string;
    pictureUrl?: string;
    issuer?: string;
    subject?: string;
    expiresAt?: number; // epoch seconds
  };
  /** Clipboard adapter for testability (defaults to navigator.clipboard.writeText when available) */
  copyToClipboard?: (text: string) => Promise<void>;
};

export default function ClaimsDisplay({ claims, fields, copyToClipboard }: ClaimsDisplayProps) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [jsonExpanded, setJsonExpanded] = useState(false);

  // Derive display fields: prefer the explicit `fields` value and fall back to the raw
  // claim. This mirrors IF50's contract — a caller that wants a row shown passes it
  // explicitly in `fields`; a caller with no opinion still gets the raw-claim fallback.
  const displaySubject = fields?.subject ?? (claims['sub'] as string | undefined);
  const displayEmail = fields?.email ?? (claims['email'] as string | undefined);
  const displayName = fields?.name ?? (claims['name'] as string | undefined);
  const displayIssuer = fields?.issuer ?? (claims['iss'] as string | undefined);
  const displayExpiresAt = fields?.expiresAt ?? (claims['exp'] as number | undefined);
  const displayPictureUrl = fields?.pictureUrl ?? (claims['picture'] as string | undefined);

  const hasDisplayFields =
    displaySubject ??
    displayEmail ??
    displayName ??
    displayIssuer ??
    displayExpiresAt ??
    displayPictureUrl;

  const handleCopy = async () => {
    const text = JSON.stringify(claims, null, 2);
    try {
      if (copyToClipboard) {
        await copyToClipboard(text);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopyFailed(true);
      setTimeout(() => setCopyFailed(false), 2200);
    }
  };

  const jsonString = JSON.stringify(claims, null, 2);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Key fields display — shown when any field is available */}
      {hasDisplayFields && (
        <GlassCard style={{ padding: '24px 28px' }}>
          <h3 style={sectionTitleStyle}>Identity Fields</h3>
          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {displayPictureUrl && (
              <div style={fieldRowStyle}>
                <span style={fieldLabelStyle}>Picture</span>
                <img
                  src={displayPictureUrl}
                  alt="Avatar"
                  style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }}
                />
              </div>
            )}
            {displayName && (
              <div style={fieldRowStyle}>
                <span style={fieldLabelStyle}>Name</span>
                <span style={fieldValueStyle}>{displayName}</span>
              </div>
            )}
            {displayEmail && (
              <div style={fieldRowStyle}>
                <span style={fieldLabelStyle}>Email</span>
                <span style={fieldValueStyle}>{displayEmail}</span>
              </div>
            )}
            {displaySubject && (
              <div style={fieldRowStyle}>
                <span style={fieldLabelStyle}>Subject</span>
                <span style={{ ...fieldValueStyle, ...monoValueStyle }}>{displaySubject}</span>
              </div>
            )}
            {displayIssuer && (
              <div style={fieldRowStyle}>
                <span style={fieldLabelStyle}>Issuer</span>
                <span style={{ ...fieldValueStyle, ...monoValueStyle }}>{displayIssuer}</span>
              </div>
            )}
            {displayExpiresAt !== undefined && (
              <div style={fieldRowStyle}>
                <span style={fieldLabelStyle}>Expires (exp)</span>
                <span style={fieldValueStyle}>
                  {new Date(displayExpiresAt * 1000).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </GlassCard>
      )}

      {/* Raw JSON viewer — pre only rendered when expanded */}
      <GlassCard style={{ padding: '24px 28px' }}>
        <div style={jsonHeaderStyle}>
          <div>
            <h3 style={sectionTitleStyle}>Raw Claims</h3>
            <p style={sectionSubtitleStyle}>Decoded ID token payload</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => void handleCopy()} style={miniCopyBtnStyle}>
              {copyFailed ? '✗ Copy failed' : copied ? '✓ Copied to clipboard' : '📋 Copy Claims JSON'}
            </button>
            <button onClick={() => setJsonExpanded(!jsonExpanded)} style={expandBtnStyle}>
              {jsonExpanded ? 'Collapse ↑' : 'Expand ↓'}
            </button>
          </div>
        </div>

        {jsonExpanded ? (
          <div style={jsonViewerStyle}>
            <pre style={jsonPreStyle}>{jsonString}</pre>
          </div>
        ) : (
          <div style={collapsedHintStyle}>
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>
              Click{' '}
              <strong style={{ color: 'rgba(255,255,255,0.5)' }}>Expand</strong> to view the full
              JSON payload
            </span>
          </div>
        )}
      </GlassCard>
    </div>
  );
}

/* ---- Styles ---- */

const sectionTitleStyle: CSSProperties = {
  fontSize: '15px',
  fontWeight: 700,
  color: '#ffffff',
  margin: '0 0 3px',
};

const sectionSubtitleStyle: CSSProperties = {
  fontSize: '12px',
  color: 'rgba(255,255,255,0.35)',
  margin: 0,
};

const fieldRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '8px',
  padding: '10px 14px',
  borderRadius: '10px',
  background: 'rgba(255,255,255,0.03)',
};

const fieldLabelStyle: CSSProperties = {
  fontSize: '12px',
  color: 'rgba(255,255,255,0.4)',
  fontWeight: 500,
  flexShrink: 0,
  minWidth: '140px',
};

const fieldValueStyle: CSSProperties = {
  fontSize: '13px',
  color: 'rgba(255,255,255,0.82)',
  fontWeight: 500,
  textAlign: 'right',
  maxWidth: '340px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const monoValueStyle: CSSProperties = {
  fontFamily: "'SF Mono', 'Fira Code', monospace",
  fontSize: '11px',
  color: 'rgba(147,197,253,0.85)',
};

const jsonHeaderStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '16px',
  gap: '12px',
  flexWrap: 'wrap',
};

const miniCopyBtnStyle: CSSProperties = {
  background: 'rgba(168,85,247,0.12)',
  border: '1px solid rgba(168,85,247,0.25)',
  borderRadius: '8px',
  padding: '6px 12px',
  fontSize: '11px',
  fontWeight: 600,
  color: '#c084fc',
  cursor: 'pointer',
};

const expandBtnStyle: CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  padding: '6px 12px',
  fontSize: '11px',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.5)',
  cursor: 'pointer',
};

const jsonViewerStyle: CSSProperties = {
  background: 'rgba(0,0,0,0.35)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: '10px',
  padding: '16px 18px',
  overflowY: 'auto',
  maxHeight: '600px',
};

const jsonPreStyle: CSSProperties = {
  fontFamily: "'SF Mono', 'Fira Code', monospace",
  fontSize: '12px',
  lineHeight: 1.65,
  margin: 0,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  color: 'rgba(134,239,172,0.9)',
};

const collapsedHintStyle: CSSProperties = {
  padding: '16px 18px',
  background: 'rgba(0,0,0,0.2)',
  border: '1px solid rgba(255,255,255,0.05)',
  borderRadius: '10px',
  textAlign: 'center',
};
